import React, { useState, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { useToastNotification } from '@/contexts/ToastContext';
import {
  Plus,
  LayoutGrid,
  List,
  Search,
  Filter,
  ChevronDown,
  GripVertical,
  Calendar,
  User,
  Tag,
  MoreHorizontal,
  Pencil,
  Trash2,
  MessageSquare,
  Clock,
  AlertCircle,
  CheckCircle2,
  Circle,
  XCircle,
  ArrowUpCircle,
  ArrowUp,
  ArrowRight,
  ArrowDown,
  Minus,
  X,
  Send
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { CalendarSingle } from '@/components/ui/calendar-single';
import { CalendarRangePicker } from '@/components/ui/calendar-range';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Task statuses configuration (Plane-inspired)
const TASK_STATUSES = [
  { id: 'backlog', label: 'Backlog', icon: Circle, color: 'text-zinc-400', bgColor: 'bg-zinc-500/10', borderColor: 'border-zinc-500/20' },
  { id: 'todo', label: 'To Do', icon: AlertCircle, color: 'text-blue-400', bgColor: 'bg-blue-500/10', borderColor: 'border-blue-500/20' },
  { id: 'in_progress', label: 'In Progress', icon: Clock, color: 'text-amber-400', bgColor: 'bg-amber-500/10', borderColor: 'border-amber-500/20' },
  { id: 'done', label: 'Done', icon: CheckCircle2, color: 'text-emerald-400', bgColor: 'bg-emerald-500/10', borderColor: 'border-emerald-500/20' },
  { id: 'cancelled', label: 'Cancelled', icon: XCircle, color: 'text-red-400', bgColor: 'bg-red-500/10', borderColor: 'border-red-500/20' },
];

// Priority configuration
const PRIORITIES = [
  { id: 'urgent', label: 'Urgent', icon: ArrowUpCircle, color: 'text-red-400', bgColor: 'bg-red-500/10' },
  { id: 'high', label: 'High', icon: ArrowUp, color: 'text-orange-400', bgColor: 'bg-orange-500/10' },
  { id: 'medium', label: 'Medium', icon: ArrowRight, color: 'text-amber-400', bgColor: 'bg-amber-500/10' },
  { id: 'low', label: 'Low', icon: ArrowDown, color: 'text-blue-400', bgColor: 'bg-blue-500/10' },
  { id: 'none', label: 'None', icon: Minus, color: 'text-zinc-400', bgColor: 'bg-zinc-500/10' },
];

// Tags will be fetched from the backend dynamically

// Helper function to format admin name as "FirstName LastInitial"
const formatAdminName = (username) => {
  if (!username) return '';
  const parts = username.trim().split(' ');
  if (parts.length === 1) return username;
  const firstName = parts[0];
  const lastInitial = parts[parts.length - 1][0];
  return `${firstName} ${lastInitial}`;
};

// Utility function for relative time
const getRelativeTime = (timestamp) => {
  if (!timestamp) return '';
  const now = new Date();
  const date = new Date(timestamp);
  const diffInSeconds = Math.floor((now - date) / 1000);
  
  if (diffInSeconds < 60) return 'Just now';
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
  if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
  return date.toLocaleDateString();
};

// Format due date
const formatDueDate = (dateStr) => {
  if (!dateStr) return null;
  const date = new Date(dateStr);
  const now = new Date();
  const diffDays = Math.floor((date - now) / (1000 * 60 * 60 * 24));
  
  if (diffDays < 0) return { text: 'Overdue', color: 'text-red-400' };
  if (diffDays === 0) return { text: 'Today', color: 'text-amber-400' };
  if (diffDays === 1) return { text: 'Tomorrow', color: 'text-blue-400' };
  if (diffDays < 7) return { text: `${diffDays}d`, color: 'text-zinc-400' };
  return { text: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }), color: 'text-zinc-400' };
};

// Task Card Component
const TaskCard = ({ task, onEdit, onDelete, onViewDetail, onDragStart, onDragEnd, isDragging }) => {
  const [isDraggingLocal, setIsDraggingLocal] = useState(false);
  const status = TASK_STATUSES.find(s => s.id === task.status) || TASK_STATUSES[0];
  const priority = PRIORITIES.find(p => p.id === task.priority) || PRIORITIES[4];
  const PriorityIcon = priority.icon;
  const dueInfo = formatDueDate(task.dueDate);
  const assignee = task.assignee;

  const handleDragStart = (e) => {
    setIsDraggingLocal(true);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', task.id);
    onDragStart(e, task);
  };

  const handleDragEnd = (e) => {
    setIsDraggingLocal(false);
    onDragEnd(e);
  };

  return (
    <div
      draggable="true"
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      className={`group bg-[#111318] border border-white/6 rounded-lg p-3 cursor-grab active:cursor-grabbing hover:border-white/12 transition-all duration-150 ${
        isDraggingLocal ? 'opacity-50 scale-95' : 'opacity-100'
      }`}
      onClick={() => onViewDetail(task)}
    >
      {/* Header with priority and menu */}
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2">
          <PriorityIcon size={14} className={priority.color} />
          <span className="text-xs text-zinc-500 font-mono">TSK-{task.id.slice(-4).toUpperCase()}</span>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
            <button className="p-1 opacity-0 group-hover:opacity-100 hover:bg-white/5 rounded transition-all">
              <MoreHorizontal size={14} className="text-zinc-400" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="bg-[#0f1115] border-white/10">
            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onEdit(task); }} className="text-zinc-300 hover:bg-white/5">
              <Pencil size={14} className="mr-2" /> Edit
            </DropdownMenuItem>
            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onDelete(task.id); }} className="text-red-400 hover:bg-red-500/10">
              <Trash2 size={14} className="mr-2" /> Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Title */}
      <h4 className="text-sm text-zinc-100 font-medium mb-2 line-clamp-2">{task.title}</h4>

      {/* Tags */}
      {task.tags && task.tags.length > 0 && (
        <div className="flex items-center gap-1 mb-2 overflow-hidden">
          {task.tags.slice(0, 2).map((tag) => (
            <span
              key={tag.id}
              className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium border whitespace-nowrap flex-shrink-0"
              style={{
                backgroundColor: `${tag.color}15`,
                color: tag.color,
                borderColor: `${tag.color}30`
              }}
            >
              {tag.name}
            </span>
          ))}
          {task.tags.length > 2 && (
            <span className="inline-flex items-center px-1.5 py-0.5 rounded-md text-xs font-medium bg-zinc-700/50 text-zinc-400 border border-zinc-600/50 whitespace-nowrap flex-shrink-0">
              +{task.tags.length - 2}
            </span>
          )}
        </div>
      )}

      {/* Footer */}
      <div className="flex items-center justify-between mt-2 pt-2 border-t border-white/5">
        <div className="flex items-center gap-2">
          {dueInfo && (
            <div className={`flex items-center gap-1 text-xs ${dueInfo.color}`}>
              <Calendar size={12} />
              <span>{dueInfo.text}</span>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          {assignee && (
            <div className="w-6 h-6 rounded-full bg-blue-500/20 flex items-center justify-center">
              <span className="text-xs text-blue-400 font-medium">
                {assignee.username.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Kanban Column Component
const KanbanColumn = ({ status, tasks, onDrop, onEdit, onDelete, onViewDetail, onDragStart, onDragEnd, dragOverColumn, onDragOver }) => {
  const StatusIcon = status.icon;
  const columnTasks = tasks.filter(t => t.status === status.id);
  const isDropTarget = dragOverColumn === status.id;

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
    onDragOver(e, status.id);
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onDrop(e, status.id);
  };

  return (
    <div className={`flex-shrink-0 w-72 transition-all duration-150 ${
      isDropTarget ? 'bg-blue-500/10 ring-2 ring-blue-500/30 ring-inset rounded-lg' : ''
    }`}>
      {/* Column Header */}
      <div className={`flex items-center justify-between mb-3 px-2 py-2 rounded-lg ${status.bgColor} border ${status.borderColor}`}>
        <div className="flex items-center gap-2">
          <StatusIcon size={16} className={status.color} />
          <span className="text-sm font-medium text-zinc-200">{status.label}</span>
          <span className="text-xs text-zinc-500 bg-white/5 px-1.5 py-0.5 rounded">
            {columnTasks.length}
          </span>
        </div>
      </div>

      {/* Tasks Container */}
      <div
        className="space-y-2 min-h-[200px] p-1 rounded-lg"
        onDragOver={handleDragOver}
        onDragEnter={handleDragEnter}
        onDrop={handleDrop}
      >
        {columnTasks.map(task => (
          <TaskCard
            key={task.id}
            task={task}
            onEdit={onEdit}
            onDelete={onDelete}
            onViewDetail={onViewDetail}
            onDragStart={onDragStart}
            onDragEnd={onDragEnd}
          />
        ))}
        {columnTasks.length === 0 && (
          <div className="text-center py-8 text-zinc-600 text-sm">
            No tasks
          </div>
        )}
      </div>
    </div>
  );
};

// List View Row Component
const TaskRow = ({ task, onEdit, onDelete, onViewDetail }) => {
  const status = TASK_STATUSES.find(s => s.id === task.status) || TASK_STATUSES[0];
  const priority = PRIORITIES.find(p => p.id === task.priority) || PRIORITIES[4];
  const StatusIcon = status.icon;
  const PriorityIcon = priority.icon;
  const dueInfo = formatDueDate(task.dueDate);
  const assignee = TEAM_MEMBERS.find(m => m.id === task.assignee);

  return (
    <motion.tr
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="group border-b border-white/5 hover:bg-white/[0.02] cursor-pointer"
      onClick={() => onViewDetail(task)}
    >
      <td className="py-3 px-4">
        <div className="flex items-center gap-3">
          <StatusIcon size={16} className={status.color} />
          <span className="text-xs text-zinc-500 font-mono">TSK-{task.id.slice(-4).toUpperCase()}</span>
        </div>
      </td>
      <td className="py-3 px-4">
        <span className="text-sm text-zinc-100">{task.title}</span>
      </td>
      <td className="py-3 px-4">
        <div className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs ${status.bgColor} ${status.color}`}>
          <StatusIcon size={12} />
          {status.label}
        </div>
      </td>
      <td className="py-3 px-4">
        <div className={`inline-flex items-center gap-1 text-xs ${priority.color}`}>
          <PriorityIcon size={14} />
          {priority.label}
        </div>
      </td>
      <td className="py-3 px-4">
        {assignee ? (
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-full bg-blue-500/20 flex items-center justify-center">
              <span className="text-xs text-blue-400 font-medium">{assignee.initials}</span>
            </div>
            <span className="text-xs text-zinc-400">{assignee.name}</span>
          </div>
        ) : (
          <span className="text-xs text-zinc-600">Unassigned</span>
        )}
      </td>
      <td className="py-3 px-4">
        {dueInfo ? (
          <span className={`text-xs ${dueInfo.color}`}>{dueInfo.text}</span>
        ) : (
          <span className="text-xs text-zinc-600">No date</span>
        )}
      </td>
      <td className="py-3 px-4">
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            onClick={(e) => { e.stopPropagation(); onEdit(task); }}
            className="p-1.5 hover:bg-white/5 rounded transition-colors"
          >
            <Pencil size={14} className="text-zinc-400" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(task.id); }}
            className="p-1.5 hover:bg-red-500/10 rounded transition-colors"
          >
            <Trash2 size={14} className="text-red-400" />
          </button>
        </div>
      </td>
    </motion.tr>
  );
};

// Create/Edit Task Modal
const TaskModal = ({ isOpen, onClose, task, onSave, availableTags = [], admins = [] }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    status: 'backlog',
    priority: 'none',
    assignee: '',
    tags: '',
    dueDate: ''
  });
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (task) {
      setFormData({
        title: task.title || '',
        description: task.description || '',
        status: task.status || 'backlog',
        priority: task.priority || 'none',
        assignee: task.assignee?.id || '',
        tags: task.tags ? task.tags.map(t => t.name).join(', ') : '',
        dueDate: task.dueDate ? task.dueDate.split('T')[0] : ''
      });
    } else {
      setFormData({
        title: '',
        description: '',
        status: 'backlog',
        priority: 'none',
        assignee: '',
        tags: '',
        dueDate: ''
      });
    }
    setErrors({});
  }, [task, isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim()) return;
    
    // Validate tags
    const tagNames = formData.tags ? formData.tags.split(',').map(t => t.trim()).filter(t => t) : [];
    const invalidTags = tagNames.filter(tagName => 
      !availableTags.some(tag => tag.name.toLowerCase() === tagName.toLowerCase())
    );
    
    if (invalidTags.length > 0) {
      setErrors({ tags: `Invalid tag(s): ${invalidTags.join(', ')}. Please use existing Task Tags.` });
      return;
    }
    
    // Convert tag names to tag IDs
    const tagIds = tagNames.map(tagName => {
      const tag = availableTags.find(t => t.name.toLowerCase() === tagName.toLowerCase());
      return tag ? tag.id : null;
    }).filter(id => id !== null);
    
    setSaving(true);
    await onSave({
      ...formData,
      tags: tagIds,
      dueDate: formData.dueDate ? new Date(formData.dueDate).toISOString() : null
    });
    setSaving(false);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-[#0f1115] border-white/10 sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-zinc-100">
            {task ? 'Edit Task' : 'Create Task'}
          </DialogTitle>
          <DialogDescription className="text-zinc-500">
            {task ? 'Update the task details below.' : 'Fill in the details to create a new task.'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Title */}
          <div>
            <Label className="text-zinc-400 text-xs">Title *</Label>
            <Input
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              placeholder="Task title"
              className="mt-1 bg-[#111318] border-white/10 text-zinc-100"
              required
            />
          </div>

          {/* Description */}
          <div>
            <Label className="text-zinc-400 text-xs">Description</Label>
            <Textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Add a description..."
              className="mt-1 bg-[#111318] border-white/10 text-zinc-100 min-h-[80px]"
            />
          </div>

          {/* Status & Priority */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-zinc-400 text-xs">Status</Label>
              <Select value={formData.status} onValueChange={(v) => setFormData(prev => ({ ...prev, status: v }))}>
                <SelectTrigger className="mt-1 bg-[#111318] border-white/10 text-zinc-100">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#111318] border-white/10">
                  {TASK_STATUSES.map(s => (
                    <SelectItem key={s.id} value={s.id} className="text-zinc-300">
                      <div className="flex items-center gap-2">
                        <s.icon size={14} className={s.color} />
                        {s.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-zinc-400 text-xs">Priority</Label>
              <Select value={formData.priority} onValueChange={(v) => setFormData(prev => ({ ...prev, priority: v }))}>
                <SelectTrigger className="mt-1 bg-[#111318] border-white/10 text-zinc-100">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#111318] border-white/10">
                  {PRIORITIES.map(p => (
                    <SelectItem key={p.id} value={p.id} className="text-zinc-300">
                      <div className="flex items-center gap-2">
                        <p.icon size={14} className={p.color} />
                        {p.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Assignee & Due Date */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-zinc-400 text-xs">Assignee</Label>
              <Select value={formData.assignee || 'unassigned'} onValueChange={(v) => setFormData(prev => ({ ...prev, assignee: v === 'unassigned' ? '' : v }))}>
                <SelectTrigger className="mt-1 bg-[#111318] border-white/10 text-zinc-100">
                  <SelectValue placeholder="Unassigned" />
                </SelectTrigger>
                <SelectContent className="bg-[#111318] border-white/10">
                  <SelectItem value="unassigned" className="text-zinc-500">Unassigned</SelectItem>
                  {admins.map(admin => (
                    <SelectItem key={admin.id} value={admin.id} className="text-zinc-300">
                      {formatAdminName(admin.username)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-zinc-400 text-xs">Due Date</Label>
              <div className="mt-1">
                <CalendarSingle
                  value={formData.dueDate ? new Date(formData.dueDate) : null}
                  onChange={(date) => setFormData(prev => ({ 
                    ...prev, 
                    dueDate: date ? date.toISOString().split('T')[0] : '' 
                  }))}
                  placeholder="Select due date"
                  className="w-full"
                />
              </div>
            </div>
          </div>

          {/* Tags */}
          <div>
            <Label className="text-zinc-400 text-xs">Tags (comma-separated)</Label>
            <Input
              value={formData.tags}
              onChange={(e) => {
                setFormData(prev => ({ ...prev, tags: e.target.value }));
                if (errors.tags) setErrors(prev => ({ ...prev, tags: '' }));
              }}
              placeholder="Bug, Feature, Urgent"
              className={`mt-1 bg-[#111318] border-white/10 text-zinc-100 ${errors.tags ? 'border-red-500' : ''}`}
            />
            {errors.tags && <p className="text-xs text-red-400 mt-1">{errors.tags}</p>}
            {availableTags.length > 0 && (
              <div className="mt-2 text-xs text-zinc-500">
                <span className="font-medium">Available:</span>{' '}
                {availableTags.map((tag, idx) => (
                  <span key={tag.id}>
                    {tag.name}
                    {idx < availableTags.length - 1 ? ', ' : ''}
                  </span>
                ))}
              </div>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={onClose} className="border-white/10 text-zinc-400">
              Cancel
            </Button>
            <Button type="submit" disabled={saving || !formData.title.trim()} className="bg-blue-600 hover:bg-blue-700">
              {saving ? 'Saving...' : task ? 'Update Task' : 'Create Task'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Task Detail Modal with Comments
const TaskDetailModal = ({ isOpen, onClose, task, onEdit, onDelete, onAddComment, onDeleteComment, comments, loadingComments }) => {
  const [newComment, setNewComment] = useState('');
  const [addingComment, setAddingComment] = useState(false);

  if (!task) return null;

  const status = TASK_STATUSES.find(s => s.id === task.status) || TASK_STATUSES[0];
  const priority = PRIORITIES.find(p => p.id === task.priority) || PRIORITIES[4];
  const StatusIcon = status.icon;
  const PriorityIcon = priority.icon;
  const assignee = task.assignee;
  const dueInfo = formatDueDate(task.dueDate);

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    setAddingComment(true);
    await onAddComment(task.id, newComment);
    setNewComment('');
    setAddingComment(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent hideClose className="bg-[#0f1115] border-white/10 sm:max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-xs text-zinc-500 font-mono">TSK-{task.id.slice(-4).toUpperCase()}</span>
              <div className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs ${status.bgColor} ${status.color}`}>
                <StatusIcon size={12} />
                {status.label}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={() => onEdit(task)} className="text-zinc-400 hover:text-zinc-200 hover:bg-white/5">
                <Pencil size={14} />
              </Button>
              <Button variant="ghost" size="sm" onClick={() => { onDelete(task.id); onClose(); }} className="text-red-400 hover:text-red-300 hover:bg-red-500/10">
                <Trash2 size={14} />
              </Button>
              <Button variant="ghost" size="sm" onClick={onClose} className="text-zinc-400 hover:text-zinc-200 hover:bg-white/5">
                <X size={14} />
              </Button>
            </div>
          </div>
          <DialogTitle className="text-zinc-100 text-lg mt-2">{task.title}</DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-4 py-4 px-1">
          {/* Description */}
          {task.description && (
            <div className="bg-[#111318] rounded-lg p-3 border border-white/5">
              <p className="text-sm text-zinc-300 whitespace-pre-wrap">{task.description}</p>
            </div>
          )}

          {/* Meta Info */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-[#111318] rounded-lg p-3 border border-white/5">
              <span className="text-xs text-zinc-500 block mb-1">Priority</span>
              <div className={`flex items-center gap-2 ${priority.color}`}>
                <PriorityIcon size={16} />
                <span className="text-sm">{priority.label}</span>
              </div>
            </div>
            <div className="bg-[#111318] rounded-lg p-3 border border-white/5">
              <span className="text-xs text-zinc-500 block mb-1">Assignee</span>
              {assignee ? (
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded-full bg-blue-500/20 flex items-center justify-center">
                    <span className="text-xs text-blue-400 font-medium">
                      {assignee.username.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                    </span>
                  </div>
                  <span className="text-sm text-zinc-300">{formatAdminName(assignee.username)}</span>
                </div>
              ) : (
                <span className="text-sm text-zinc-500">Unassigned</span>
              )}
            </div>
            {dueInfo && (
              <div className="bg-[#111318] rounded-lg p-3 border border-white/5">
                <span className="text-xs text-zinc-500 block mb-1">Due Date</span>
                <div className={`flex items-center gap-2 ${dueInfo.color}`}>
                  <Calendar size={16} />
                  <span className="text-sm">{dueInfo.text}</span>
                </div>
              </div>
            )}
            <div className="bg-[#111318] rounded-lg p-3 border border-white/5">
              <span className="text-xs text-zinc-500 block mb-1">Created</span>
              <span className="text-sm text-zinc-300">{getRelativeTime(task.createdAt)}</span>
            </div>
          </div>

          {/* Tags */}
          {task.tags && task.tags.length > 0 && (
            <div>
              <span className="text-xs text-zinc-500 block mb-2">Tags</span>
              <div className="flex flex-wrap gap-2">
                {task.tags.map(tag => (
                  <Badge 
                    key={tag.id}
                    className="border"
                    style={{
                      backgroundColor: `${tag.color}15`,
                      color: tag.color,
                      borderColor: `${tag.color}30`
                    }}
                  >
                    {tag.name}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Comments Section */}
          <div className="border-t border-white/5 pt-4">
            <h4 className="text-sm font-medium text-zinc-300 mb-3 flex items-center gap-2">
              <MessageSquare size={16} />
              Comments
            </h4>

            {/* Comment Input */}
            <div className="flex gap-2 mb-4">
              <Input
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder="Add a comment..."
                className="flex-1 bg-[#111318] border-white/10 text-zinc-100 focus-visible:ring-1 focus-visible:ring-blue-500 focus-visible:ring-offset-0"
                onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleAddComment()}
              />
              <Button
                onClick={handleAddComment}
                disabled={!newComment.trim() || addingComment}
                className="bg-blue-600 hover:bg-blue-700 flex-shrink-0 h-9 px-3"
              >
                <Send size={14} />
              </Button>
            </div>

            {/* Comments List */}
            <div className="space-y-3 max-h-[200px] overflow-y-auto">
              {loadingComments ? (
                <div className="text-center py-4 text-zinc-500 text-sm">Loading comments...</div>
              ) : comments.length === 0 ? (
                <div className="text-center py-4 text-zinc-600 text-sm">No comments yet</div>
              ) : (
                comments.map(comment => (
                  <div key={comment.id} className="bg-[#111318] rounded-lg p-3 border border-white/5 group">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-2 mb-1">
                        <div className="w-6 h-6 rounded-full bg-purple-500/20 flex items-center justify-center">
                          <span className="text-xs text-purple-400 font-medium">
                            {comment.author?.slice(0, 2).toUpperCase() || 'AN'}
                          </span>
                        </div>
                        <span className="text-xs text-zinc-400">{comment.author}</span>
                        <span className="text-xs text-zinc-600">·</span>
                        <span className="text-xs text-zinc-600">{getRelativeTime(comment.createdAt)}</span>
                      </div>
                      <button
                        onClick={() => onDeleteComment(task.id, comment.id)}
                        className="p-1 opacity-0 group-hover:opacity-100 hover:bg-red-500/10 rounded transition-all"
                      >
                        <Trash2 size={12} className="text-red-400" />
                      </button>
                    </div>
                    <p className="text-sm text-zinc-300 ml-8">{comment.content}</p>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Main Tasks Page Component
const Tasks = () => {
  const toast = useToastNotification();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('kanban'); // 'kanban' or 'list'
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [dateRange, setDateRange] = useState(null);
  const [availableTags, setAvailableTags] = useState([]);
  const [admins, setAdmins] = useState([]);
  
  // Modal states
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);
  const [comments, setComments] = useState([]);
  const [loadingComments, setLoadingComments] = useState(false);

  // Drag state
  const [draggedTask, setDraggedTask] = useState(null);
  const [dragOverColumn, setDragOverColumn] = useState(null);

  // Fetch available task tags
  const fetchTags = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/tags?tag_type=tasks`, { withCredentials: true });
      setAvailableTags(response.data);
    } catch (error) {
      console.error('Error fetching tags:', error);
    }
  }, []);

  // Fetch admins
  const fetchAdmins = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/users`, { withCredentials: true });
      setAdmins(response.data);
    } catch (error) {
      console.error('Error fetching admins:', error);
    }
  }, []);

  // Fetch tasks
  const fetchTasks = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/tasks`, { withCredentials: true });
      setTasks(response.data);
    } catch (error) {
      console.error('Error fetching tasks:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTags();
    fetchAdmins();
    fetchTasks();
  }, [fetchTags, fetchAdmins, fetchTasks]);

  // Fetch comments for a task
  const fetchComments = useCallback(async (taskId) => {
    setLoadingComments(true);
    try {
      const response = await axios.get(`${API_URL}/api/tasks/${taskId}/comments`, { withCredentials: true });
      setComments(response.data);
    } catch (error) {
      console.error('Error fetching comments:', error);
      setComments([]);
    } finally {
      setLoadingComments(false);
    }
  }, []);

  // Create task
  const handleCreateTask = useCallback(async (taskData) => {
    try {
      const response = await axios.post(`${API_URL}/api/tasks`, taskData, { withCredentials: true });
      setTasks(prev => [response.data, ...prev]);
      toast.success('Task created');
    } catch (error) {
      console.error('Error creating task:', error);
      toast.error('Failed to create task');
    }
  }, [toast]);

  // Update task
  const handleUpdateTask = useCallback(async (taskData) => {
    if (!editingTask) return;
    try {
      const response = await axios.put(`${API_URL}/api/tasks/${editingTask.id}`, taskData, { withCredentials: true });
      setTasks(prev => prev.map(t => t.id === editingTask.id ? response.data : t));
      if (selectedTask?.id === editingTask.id) {
        setSelectedTask(response.data);
      }
      toast.success('Task updated');
    } catch (error) {
      console.error('Error updating task:', error);
      toast.error('Failed to update task');
    }
  }, [editingTask, selectedTask, toast]);

  // Delete task
  const handleDeleteTask = useCallback(async (taskId) => {
    try {
      await axios.delete(`${API_URL}/api/tasks/${taskId}`, { withCredentials: true });
      setTasks(prev => prev.filter(t => t.id !== taskId));
      toast.success('Task deleted');
    } catch (error) {
      console.error('Error deleting task:', error);
      toast.error('Failed to delete task');
    }
  }, [toast]);

  // Add comment
  const handleAddComment = useCallback(async (taskId, content) => {
    try {
      const response = await axios.post(
        `${API_URL}/api/tasks/${taskId}/comments`,
        { content, author: 'Current User' },
        { withCredentials: true }
      );
      setComments(prev => [response.data, ...prev]);
      toast.success('Comment added');
    } catch (error) {
      console.error('Error adding comment:', error);
      toast.error('Failed to add comment');
    }
  }, [toast]);

  // Delete comment
  const handleDeleteComment = useCallback(async (taskId, commentId) => {
    try {
      await axios.delete(`${API_URL}/api/tasks/${taskId}/comments/${commentId}`, { withCredentials: true });
      setComments(prev => prev.filter(c => c.id !== commentId));
      toast.success('Comment deleted');
    } catch (error) {
      console.error('Error deleting comment:', error);
      toast.error('Failed to delete comment');
    }
  }, [toast]);

  // View task detail
  const handleViewDetail = useCallback((task) => {
    setSelectedTask(task);
    fetchComments(task.id);
  }, [fetchComments]);

  // Edit task
  const handleEdit = useCallback((task) => {
    setEditingTask(task);
    setIsCreateModalOpen(true);
  }, []);

  // Drag handlers
  const handleDragStart = useCallback((e, task) => {
    setDraggedTask(task);
    setDragOverColumn(task.status); // Set initial column
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', task.id);
  }, []);

  const handleDragEnd = useCallback(() => {
    setDraggedTask(null);
    setDragOverColumn(null);
  }, []);

  const handleDragOver = useCallback((e, statusId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverColumn(statusId);
  }, []);

  const handleDrop = useCallback(async (e, targetStatus) => {
    e.preventDefault();
    const taskId = e.dataTransfer.getData('text/plain');
    
    if (taskId) {
      // Optimistic update
      setTasks(prev => prev.map(task => 
        task.id === taskId 
          ? { ...task, status: targetStatus }
          : task
      ));
      
      // Update in backend
      try {
        const response = await axios.put(`${API_URL}/api/tasks/${taskId}`, { status: targetStatus }, { withCredentials: true });
        setTasks(prev => prev.map(t => t.id === taskId ? response.data : t));
      } catch (error) {
        console.error('Error updating task status:', error);
        fetchTasks(); // Revert on error
      }
    }
    
    setDraggedTask(null);
    setDragOverColumn(null);
  }, [fetchTasks]);

  // Filter tasks
  const filteredTasks = tasks.filter(task => {
    if (searchQuery && !task.title.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    if (filterStatus !== 'all' && task.status !== filterStatus) return false;
    if (filterPriority !== 'all' && task.priority !== filterPriority) return false;
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-zinc-500">Loading tasks...</div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-heading font-semibold text-zinc-100">Tasks</h1>
          <p className="text-zinc-500 text-sm mt-1">Manage and track your team's work</p>
        </div>
        <Button onClick={() => { setEditingTask(null); setIsCreateModalOpen(true); }} className="bg-blue-600 hover:bg-blue-700">
          <Plus size={16} className="mr-2" />
          New Task
        </Button>
      </div>

      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        {/* Search & Filters */}
        <div className="flex flex-wrap gap-2 items-center">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search tasks..."
              className="pl-9 w-64 bg-[#111318] border-white/10 text-zinc-100"
            />
          </div>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-36 bg-[#111318] border-white/10 text-zinc-300">
              <Filter size={14} className="mr-2" />
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent className="bg-[#111318] border-white/10">
              <SelectItem value="all" className="text-zinc-300">All Status</SelectItem>
              {TASK_STATUSES.map(s => (
                <SelectItem key={s.id} value={s.id} className="text-zinc-300">{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filterPriority} onValueChange={setFilterPriority}>
            <SelectTrigger className="w-36 bg-[#111318] border-white/10 text-zinc-300">
              <SelectValue placeholder="Priority" />
            </SelectTrigger>
            <SelectContent className="bg-[#111318] border-white/10">
              <SelectItem value="all" className="text-zinc-300">All Priority</SelectItem>
              {PRIORITIES.map(p => (
                <SelectItem key={p.id} value={p.id} className="text-zinc-300">{p.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <CalendarRangePicker
            value={dateRange}
            onChange={setDateRange}
            placeholder="Filter by date"
          />
        </div>

        {/* View Toggle */}
        <div className="flex items-center gap-1 bg-[#111318] rounded-lg p-1 border border-white/10">
          <button
            onClick={() => setView('kanban')}
            className={`p-2 rounded transition-colors ${view === 'kanban' ? 'bg-white/10 text-zinc-100' : 'text-zinc-500 hover:text-zinc-300'}`}
          >
            <LayoutGrid size={16} />
          </button>
          <button
            onClick={() => setView('list')}
            className={`p-2 rounded transition-colors ${view === 'list' ? 'bg-white/10 text-zinc-100' : 'text-zinc-500 hover:text-zinc-300'}`}
          >
            <List size={16} />
          </button>
        </div>
      </div>

      {/* Content */}
      {view === 'kanban' ? (
        // Kanban View
        <div className="flex gap-4 overflow-x-auto pb-4">
          {TASK_STATUSES.map(status => (
            <KanbanColumn
              key={status.id}
              status={status}
              tasks={filteredTasks}
              onDrop={handleDrop}
              onEdit={handleEdit}
              onDelete={handleDeleteTask}
              onViewDetail={handleViewDetail}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
              onDragOver={handleDragOver}
              dragOverColumn={dragOverColumn}
            />
          ))}
        </div>
      ) : (
        // List View
        <div className="bg-[#0f1115] rounded-lg border border-white/6 overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/10 bg-[#111318]">
                <th className="py-3 px-4 text-left text-xs font-medium text-zinc-500 uppercase tracking-wider">ID</th>
                <th className="py-3 px-4 text-left text-xs font-medium text-zinc-500 uppercase tracking-wider">Title</th>
                <th className="py-3 px-4 text-left text-xs font-medium text-zinc-500 uppercase tracking-wider">Status</th>
                <th className="py-3 px-4 text-left text-xs font-medium text-zinc-500 uppercase tracking-wider">Priority</th>
                <th className="py-3 px-4 text-left text-xs font-medium text-zinc-500 uppercase tracking-wider">Assignee</th>
                <th className="py-3 px-4 text-left text-xs font-medium text-zinc-500 uppercase tracking-wider">Due</th>
                <th className="py-3 px-4 text-left text-xs font-medium text-zinc-500 uppercase tracking-wider w-20"></th>
              </tr>
            </thead>
            <tbody>
              <AnimatePresence>
                {filteredTasks.map(task => (
                  <TaskRow
                    key={task.id}
                    task={task}
                    onEdit={handleEdit}
                    onDelete={handleDeleteTask}
                    onViewDetail={handleViewDetail}
                  />
                ))}
              </AnimatePresence>
            </tbody>
          </table>
          {filteredTasks.length === 0 && (
            <div className="text-center py-12 text-zinc-500">
              No tasks found. Create your first task to get started.
            </div>
          )}
        </div>
      )}

      {/* Create/Edit Modal */}
      <TaskModal
        isOpen={isCreateModalOpen}
        onClose={() => { setIsCreateModalOpen(false); setEditingTask(null); }}
        task={editingTask}
        onSave={editingTask ? handleUpdateTask : handleCreateTask}
        availableTags={availableTags}
        admins={admins}
      />

      {/* Task Detail Modal */}
      <TaskDetailModal
        isOpen={!!selectedTask}
        onClose={() => setSelectedTask(null)}
        task={selectedTask}
        onEdit={handleEdit}
        onDelete={handleDeleteTask}
        onAddComment={handleAddComment}
        onDeleteComment={handleDeleteComment}
        comments={comments}
        loadingComments={loadingComments}
      />
    </motion.div>
  );
};

export default Tasks;
